/**
 * Estado de pago armado desde las guías cargadas.
 * Es el cierre del círculo: nada se transcribe a mano.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Lock, LockOpen, FileSpreadsheet, TriangleAlert, X } from 'lucide-react';
import {
  Alerta,
  Boton,
  Campo,
  Chip,
  Combo,
  Indicador,
  Tarjeta,
  TarjetaTitulo,
  Vacio,
} from '@/components/ui';
import type { DatosEP } from '@/db/exportarExcel';
import { listar } from '@/db/repositorio';
import { useCatalogos } from '@/hooks/useCatalogos';
import { useAuth } from '@/hooks/useAuth';
import { useDialogo } from '@/components/ui/dialogo';
import { armarEstadoPago, resumenHes, type GuiaConRecargos } from '@/domain/estadoPago';
import { calcularRecargos } from '@/domain/recargos';
import { calcularTiempos } from '@/domain/tiempos';
import { generaRecargos, normalizarCodigo, parsearProducto } from '@/domain/producto';
import { aFechaISO, clp, fecha as ffecha, uf } from '@/domain/formato';
import { controlarContrato } from '@/domain/precios';
import { lazy, Suspense } from 'react';
import { cn } from '@/lib/cn';
import { useGuias } from '@/hooks/useGuias';
import { Pendientes } from '@/components/Pendientes';
import { mensajeFirestore } from '@/db/errores';
import { useIndicadores } from '@/hooks/useIndicadores';
import { leerContrato, type DocContrato } from '@/db/contrato';
import {
  acumuladoAntesDe,
  cerrarEstadoPago,
  epDelNumero,
  listarEstadosPago,
  problemasParaCerrar,
  reabrirEstadoPago,
  ajustesFijosPrevios,
  inicioHistoria,
  type AjusteEP,
  type DocEstadoPago,
} from '@/db/estadosPago';

const ExportarPdf = lazy(() => import('./ExportarPdf'));
const Plantilla = lazy(() => import('./Plantilla'));

function inicioDeMes(): string {
  const d = new Date();
  return aFechaISO(new Date(d.getFullYear(), d.getMonth(), 1));
}

export default function EstadoPago() {
  const { catalogos, buscarItem } = useCatalogos();
  const { perfil, puede } = useAuth();
  const { confirmar, pedirTexto } = useDialogo();
  const { indicadores } = useIndicadores();
  const { guias } = useGuias();
  const [desde, setDesde] = useState(inicioDeMes());
  const [hasta, setHasta] = useState(aFechaISO(new Date()));
  /** Se completa con la UF del contrato en cuanto carga. */
  const [valorUf, setValorUf] = useState('');
  const [factorGg, setFactorGg] = useState('1');

  // Los montos del contrato son un dato administrado. Sin ellos, el estado de
  // pago no puede declarar cuánto se lleva consumido — y este documento se le
  // manda a CODELCO, así que no se rellena con supuestos.
  const [contratoDoc, setContratoDoc] = useState<DocContrato | null>(null);

  useEffect(() => {
    void leerContrato().then((c) => {
      setContratoDoc(c);
      // La UF del contrato es el punto de partida; se ajusta al cierre.
      if (c)
        setValorUf((v) => v || c.valorUf.toLocaleString('es-CL', { maximumFractionDigits: 3 }));
    });
  }, []);

  /** Estados de pago ya guardados: dan el acumulado anterior y la cadena. */
  const [eps, setEps] = useState<DocEstadoPago[]>([]);

  /**
   * Cantidades que no vienen del libro de guías.
   *
   * El suministro desde Valle se anota directo en el avance físico-financiero
   * de la planilla, sin guía de por medio, y lo mismo pasa con cualquier
   * corrección de un período. Cada una lleva motivo: dentro de un mes nadie se
   * acuerda de por qué había 69,5 m³ sueltos en la partida 3.24.
   */
  const [ajustes, setAjustes] = useState<Record<string, AjusteEP>>({});
  const [ajCodigo, setAjCodigo] = useState('');
  const [ajCantidad, setAjCantidad] = useState('');
  const [ajMotivo, setAjMotivo] = useState('');
  const [ajFijo, setAjFijo] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [avisoCierre, setAvisoCierre] = useState<string | null>(null);
  const [errorCierre, setErrorCierre] = useState<string[]>([]);

  const recargarEps = useCallback(async () => {
    setEps(await listarEstadosPago());
  }, []);

  useEffect(() => {
    void recargarEps();
  }, [recargarEps]);

  /**
   * El período arranca donde terminó el EP anterior.
   *
   * La fecha de corte no es fin de mes: según el caso se cierra el 19, el 20
   * o el 21. Encadenar desde el último cierre evita dejar días sin facturar o
   * repetirlos, que es lo que pasa al escribir las fechas de memoria.
   * Sólo se propone una vez, al entrar; después manda lo que elija el usuario.
   */
  const periodoPropuesto = useRef(false);
  useEffect(() => {
    if (periodoPropuesto.current || eps.length === 0) return;
    const cerrados = eps
      .filter((e) => e.estado !== 'ABIERTO')
      .sort((a, b) => a.periodoHasta.localeCompare(b.periodoHasta));
    const ultimo = cerrados.at(-1);
    if (!ultimo) return;
    periodoPropuesto.current = true;
    const d = new Date(`${ultimo.periodoHasta}T00:00:00`);
    d.setDate(d.getDate() + 1);
    setDesde(aFechaISO(d));
    setNumeroEp(String(ultimo.numero + 1));
    // Lo que se cobra todos los meses viene propuesto; se puede quitar.
    setAjustes(ajustesFijosPrevios(eps, ultimo.numero + 1));
  }, [eps]);
  const [expandido, setExpandido] = useState<number | null>(null);
  /**
   * Vacío a propósito. Estuvo fijo en «59» y eso presumía en qué EP va la
   * empresa: al empezar de cero había que cerrar el 58 y el campo proponía
   * otro número. Cuando hay un EP cerrado, el siguiente se propone solo; si
   * no hay ninguno, lo escribe quien sabe cuál corresponde.
   */
  const [numeroEp, setNumeroEp] = useState('');
  const [gastosGenerales, setGastosGenerales] = useState<
    {
      grupo: string;
      descripcion: string;
      unidad: string;
      cantidadContratada: number;
      precioUnitarioUf: number;
      orden: number;
    }[]
  >([]);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    void listar<(typeof gastosGenerales)[number] & { id: string }>('gastosGenerales').then((g) =>
      setGastosGenerales(g.sort((a, b) => a.orden - b.orden)),
    );
  }, []);

  const ufNum = Number(valorUf.replace(/\./g, '').replace(',', '.')) || 0;

  /** Recalcula tiempos y recargos de cada guía con el motor de dominio. */
  const guiasConRecargos: GuiaConRecargos[] = useMemo(() => {
    return guias.map((g) => {
      const planta = catalogos.plantas.find((p) => p.codigoPlanta === g.plantaCodigo);
      /**
       * El producto sirve para los recargos, y el código se interpreta solo.
       * Buscarlo únicamente en el catálogo dejaba sin recargos a las
       * combinaciones nuevas —«GB30(90)20-13 AIR R7 ADI»—: trece guías de
       * Valle perdían su aire, su R7 y su sobrestadía, 146.934 UF.
       */
      const producto =
        catalogos.productos.find((p) => p.id === g.productoId) ??
        catalogos.productos.find((p) => p.codigo === g.productoCodigo);
      const codigoProd = producto?.codigo ?? g.productoCodigo;
      const prodParseado = codigoProd ? parsearProducto(codigoProd) : null;
      const d = (s: string | null) => (s ? new Date(s) : undefined);

      const tiempos = calcularTiempos({
        salidaPlanta: d(g.tsSalidaPlanta),
        llegadaObra: d(g.tsLlegadaObra),
        inicioDescarga: d(g.tsInicioDescarga),
        terminoDescarga: d(g.tsTerminoDescarga),
        salidaObra: d(g.tsSalidaObra),
        llegadaPlanta: d(g.tsLlegadaPlanta),
      });

      const recargos =
        planta && codigoProd && prodParseado
          ? calcularRecargos(
              {
                plantaCodigo: g.plantaCodigo,
                minCargaM3: planta.minCargaM3,
                cantidadM3: g.cantidadM3,
                producto: prodParseado,
                generaRecargos: producto
                  ? producto.generaRecargos
                  : generaRecargos(codigoProd, prodParseado),
                tiempos,
                codigoProductoNormalizado:
                  producto?.codigoNormalizado ?? normalizarCodigo(codigoProd),
              },
              buscarItem,
            )
          : [];

      return {
        guia: {
          id: g.id,
          folio: g.folio,
          fecha: g.fecha,
          cantidadM3: g.cantidadM3,
          unidadCantidad: g.unidadCantidad ?? 'M3',
          tipoVenta: g.tipoVenta ?? 'CONTRATO',
          itemContratoId: g.itemContratoId,
          estadoPagoId: g.estadoPagoId ?? null,
          epDeclarado: g.epDeclarado ?? null,
          soloConsulta: g.soloConsulta ?? false,
          estado: g.estado,
        },
        recargos,
      };
    });
  }, [guias, catalogos, buscarItem]);

  const ep = useMemo(
    () =>
      armarEstadoPago({
        numero: Number(numeroEp) || undefined,
        periodoDesde: desde,
        periodoHasta: hasta,
        valorUf: ufNum,
        items: catalogos.items,
        capitulos: catalogos.capitulos,
        guias: guiasConRecargos,
        acumuladoAnterior: acumuladoAntesDe(eps, desde),
        inicioHistoria: inicioHistoria(eps),
        ajustes,
        factorGastosGenerales: Number(factorGg.replace(',', '.')) || 0,
      }),
    [numeroEp, desde, hasta, ufNum, catalogos, guiasConRecargos, factorGg, eps, ajustes],
  );

  /** Guías del período, que son las que entran al cierre. */
  const guiasDelPeriodo = useMemo(
    () => guias.filter((g) => g.fecha >= desde && g.fecha <= hasta && g.estado !== 'ANULADA'),
    [guias, desde, hasta],
  );

  /** El EP que se está editando, identificado por su número. */
  const epCerrado = useMemo(() => epDelNumero(eps, Number(numeroEp) || 0), [eps, numeroEp]);
  const bloqueado = Boolean(epCerrado && epCerrado.estado !== 'ABIERTO');

  /**
   * Al mirar un EP cerrado, el período que se muestra es el suyo.
   *
   * Sin esto la pantalla mezclaba: las cifras del EP 58 bajo el encabezado
   * «21-09 a 24-09», que eran las fechas que habían quedado en los campos.
   * Un documento emitido se mira entero o no se mira.
   */
  useEffect(() => {
    if (!bloqueado || !epCerrado) return;
    setDesde(epCerrado.periodoDesde);
    setHasta(epCerrado.periodoHasta);
    setValorUf(epCerrado.valorUf.toLocaleString('es-CL', { maximumFractionDigits: 3 }));
    setAjustes(epCerrado.ajustes ?? {});
  }, [bloqueado, epCerrado]);

  /**
   * Lo que se muestra. Un EP cerrado no se recalcula: se lee el documento tal
   * como se emitió, aunque después hayan cambiado precios o guías.
   */
  const epMostrado = useMemo(
    () =>
      bloqueado && epCerrado ? { ...ep, lineas: epCerrado.lineas, totales: epCerrado.totales } : ep,
    [ep, epCerrado, bloqueado],
  );

  const hes = useMemo(
    () => resumenHes(epMostrado, catalogos.capitulos),
    [epMostrado, catalogos.capitulos],
  );
  const contrato = contratoDoc ? controlarContrato({ ...contratoDoc, valorUf: ufNum }) : null;

  async function cerrar() {
    setErrorCierre([]);
    setAvisoCierre(null);

    const numero = Number(numeroEp) || 0;
    const problemas = problemasParaCerrar(
      {
        numero,
        periodoDesde: desde,
        periodoHasta: hasta,
        guiaIds: guiasDelPeriodo.map((g) => g.id),
      },
      eps,
      guiasDelPeriodo.map((g) => ({ id: g.id, estadoPagoId: g.estadoPagoId })),
      epCerrado?.id ?? null,
    );
    if (problemas.length) {
      setErrorCierre(problemas);
      return;
    }

    const si = await confirmar({
      titulo: `Cerrar el EP N° ${numero}`,
      mensaje:
        `Se guardan las ${lineasConMovimiento.length} partidas con movimiento del ${ffecha(desde)} ` +
        `al ${ffecha(hasta)}, con los precios y la UF de hoy, y ${guiasACerrar.length} guías ` +
        'quedan marcadas como facturadas. Desde ahí el EP deja de recalcularse. ' +
        'Se puede reabrir cuando haga falta.',
      aceptar: 'Cerrar el EP',
    });
    if (!si) return;

    setCerrando(true);
    try {
      await cerrarEstadoPago(
        {
          numero,
          periodoDesde: desde,
          periodoHasta: hasta,
          valorUf: ufNum,
          factorGastosGenerales: Number(factorGg.replace(',', '.')) || 0,
          lineas: ep.lineas,
          totales: ep.totales,
          // Las atrasadas también se marcan: si no, el EP siguiente volvería
          // a arrastrarlas y se cobrarían dos veces.
          guiaIds: guiasACerrar,
          ajustes,
        },
        perfil?.uid ?? null,
        epCerrado,
      );
      await recargarEps();
      setAvisoCierre(`EP N° ${numero} cerrado.`);
    } catch (e) {
      setErrorCierre([mensajeFirestore(e, 'No se pudo cerrar el estado de pago.')]);
    } finally {
      setCerrando(false);
    }
  }

  async function reabrir() {
    if (!epCerrado) return;
    const motivo = await pedirTexto({
      titulo: `Reabrir el EP N° ${epCerrado.numero}`,
      mensaje:
        'Vuelve a quedar editable y sus guías dejan de estar marcadas como facturadas. ' +
        'Queda registrado quién lo reabrió y por qué.',
      etiqueta: 'Motivo',
      marcador: 'Ej.: faltaron 3 guías del día 18',
      aceptar: 'Reabrir',
      tono: 'peligro',
    });
    if (!motivo?.trim()) return;

    setCerrando(true);
    setErrorCierre([]);
    try {
      await reabrirEstadoPago(epCerrado, motivo.trim(), perfil?.uid ?? null);
      await recargarEps();
      setAvisoCierre(`EP N° ${epCerrado.numero} reabierto.`);
    } catch (e) {
      setErrorCierre([mensajeFirestore(e, 'No se pudo reabrir el estado de pago.')]);
    } finally {
      setCerrando(false);
    }
  }

  /**
   * Las cuotas que se cobran todos los meses se proponen al abrir cada EP.
   *
   * Antes esto sólo corría una vez, al cargar la pantalla, con el EP que
   * tocara en ese momento. Al escribir otro número no pasaba nada y había que
   * volver a teclear el arriendo de radios y los operadores de apoyo. Se
   * propone una vez por número: si se quitan, no vuelven solas.
   */
  const fijosPropuestos = useRef<number | null>(null);
  useEffect(() => {
    const n = Number(numeroEp) || 0;
    if (!n || bloqueado || fijosPropuestos.current === n) return;
    if (Object.keys(ajustes).length > 0) {
      fijosPropuestos.current = n;
      return;
    }
    const fijos = ajustesFijosPrevios(eps, n);
    if (Object.keys(fijos).length === 0) return;
    fijosPropuestos.current = n;
    setAjustes(fijos);
  }, [numeroEp, bloqueado, eps, ajustes]);

  /** Todo lo que este EP deja facturado: lo del período más lo atrasado. */
  const guiasACerrar = useMemo(
    () => [...new Set([...guiasDelPeriodo.map((g) => g.id), ...epMostrado.rezagadas])],
    [guiasDelPeriodo, epMostrado.rezagadas],
  );

  const lineasConMovimiento = useMemo(
    () => epMostrado.lineas.filter((l) => l.cantPeriodo !== 0),
    [epMostrado.lineas],
  );

  /**
   * Datos del EP, compartidos por el exportador de Excel y el de PDF.
   * Sólo se llama con el contrato ya configurado: los botones que exportan
   * están deshabilitados hasta entonces.
   */
  const datosEp = (): DatosEP => {
    if (!contrato) throw new Error('Falta configurar el contrato en Administración.');
    return {
      numero: Number(numeroEp) || 0,
      periodoDesde: ffecha(desde),
      periodoHasta: ffecha(hasta),
      mesReferencia: ffecha(hasta),
      valorUf: ufNum,
      fechaValorUf: ffecha(hasta),
      lineas: epMostrado.lineas,
      totales: epMostrado.totales,
      capitulos: catalogos.capitulos,
      contrato,
      gastosGenerales,
      factorGastosGenerales: Number(factorGg.replace(',', '.')) || 0,
      guias: guias.filter((g) => g.fecha >= desde && g.fecha <= hasta),
    };
  };

  /** Celdas de entrada del maestro: el resto lo calculan sus propias fórmulas. */
  const datosPlantilla = () => ({
    numeroEp: Number(numeroEp) || 0,
    periodoDesde: desde,
    periodoHasta: hasta,
    mesReferencia: hasta,
    valorUf: ufNum,
    guias: guias
      .filter((g) => g.fecha >= desde && g.fecha <= hasta && g.estado !== 'BORRADOR')
      .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.folio - b.folio),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Estado de pago</h1>
          <p className="text-xs text-texto-mute">
            Armado automáticamente desde las guías del período
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Boton
            variante="primario"
            icono={<FileSpreadsheet size={15} />}
            disabled={exportando || !lineasConMovimiento.length || !contrato}
            onClick={() => {
              setExportando(true);
              void import('@/db/exportarExcel')
                .then((m) => m.exportarEstadoPago(datosEp()))
                .finally(() => setExportando(false));
            }}
          >
            {exportando ? 'Generando…' : 'Descargar Excel'}
          </Boton>
        </div>
      </div>

      <Tarjeta>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Campo
            etiqueta="N° de EP"
            name="numeroEp"
            inputMode="numeric"
            value={numeroEp}
            onChange={(e) => setNumeroEp(e.target.value.replace(/\D/g, ''))}
            placeholder="58"
            className="font-mono"
          />
          <Campo
            etiqueta="Período desde"
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
          <Campo
            etiqueta="Período hasta"
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
          <Campo
            etiqueta="Valor UF del cierre"
            inputMode="decimal"
            value={valorUf}
            onChange={(e) => setValorUf(e.target.value)}
            sufijo="$"
            className="font-mono"
            disabled={bloqueado}
          />
          {/* La UF del día se ofrece, no se impone: el contrato puede usar un
              valor acordado distinto al de hoy. */}
          {!bloqueado && indicadores?.uf && (
            <button
              type="button"
              onClick={() =>
                setValorUf(
                  indicadores.uf!.valor.toLocaleString('es-CL', { maximumFractionDigits: 2 }),
                )
              }
              className="-mt-2 self-start text-[11px] text-petroleo-300 underline-offset-2 hover:underline"
            >
              Usar la UF de hoy (
              {indicadores.uf.valor.toLocaleString('es-CL', { minimumFractionDigits: 2 })})
            </button>
          )}
          <Campo
            etiqueta="Cuota gastos generales"
            inputMode="decimal"
            value={factorGg}
            onChange={(e) => setFactorGg(e.target.value)}
            sufijo="mes"
            className="font-mono"
            disabled={bloqueado}
          />
        </div>
      </Tarjeta>

      <Tarjeta>
        <TarjetaTitulo
          accion={
            Object.keys(ajustes).length > 0 ? (
              <Chip nivel="AMARILLO">{Object.keys(ajustes).length} partidas</Chip>
            ) : undefined
          }
        >
          Cantidades sin guía
        </TarjetaTitulo>

        <div className="space-y-3 p-4">
          <p className="text-xs leading-relaxed text-texto-mute">
            Para lo que no pasa por el libro de guías: el suministro desde Valle, que se anota
            directo en el avance físico-financiero, y las correcciones de un período. Se suma a lo
            que traigan las guías de esa misma partida.
          </p>

          {Object.entries(ajustes).length > 0 && (
            <div className="space-y-1.5">
              {Object.entries(ajustes).map(([codigo, a]) => {
                const it = catalogos.items.find((x) => x.codigo === codigo);
                return (
                  <div
                    key={codigo}
                    className="flex flex-wrap items-center gap-2 rounded-control border border-linea bg-petroleo-800/40 px-3 py-2 text-xs"
                  >
                    <span className="font-mono">{codigo}</span>
                    <span className="min-w-32 flex-1 truncate text-texto-mute">
                      {it?.descripcion ?? 'partida desconocida'}
                    </span>
                    <span className="font-mono tabular-nums">
                      {uf(a.cantidad)} {it?.unidad ?? ''}
                    </span>
                    <span className="min-w-24 flex-1 truncate text-texto-mute">{a.motivo}</span>
                    {a.fijo && (
                      <Chip nivel="VERDE" conPunto={false}>
                        todos los meses
                      </Chip>
                    )}
                    {!bloqueado && (
                      <button
                        type="button"
                        onClick={() =>
                          setAjustes((p) => {
                            const { [codigo]: _, ...resto } = p;
                            return resto;
                          })
                        }
                        className="text-texto-mute transition-colors hover:text-fluor-rojo"
                        aria-label={`Quitar el ajuste de ${codigo}`}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!bloqueado && (
            <div className="grid gap-2 sm:grid-cols-[1fr_8rem_1fr_auto]">
              <Combo
                etiqueta="Partida"
                name="ajuste-partida"
                value={ajCodigo}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAjCodigo(e.target.value)}
                placeholder="Ej.: 3.24"
                opciones={catalogos.items.map((i) => ({
                  valor: i.codigo,
                  detalle: `${i.descripcion} (${i.unidad})`,
                }))}
              />
              <Campo
                etiqueta="Cantidad"
                name="ajuste-cantidad"
                inputMode="decimal"
                value={ajCantidad}
                onChange={(e) => setAjCantidad(e.target.value.replace(/[^\d.,-]/g, ''))}
                className="font-mono"
              />
              <div>
                <Campo
                  etiqueta="Motivo"
                  name="ajuste-motivo"
                  value={ajMotivo}
                  onChange={(e) => setAjMotivo(e.target.value)}
                  placeholder="Ej.: cuota mensual de radios"
                />
                <label className="mt-1.5 flex items-center gap-2 text-[11px] text-texto-mute">
                  <input
                    type="checkbox"
                    name="ajuste-fijo"
                    checked={ajFijo}
                    onChange={(e) => setAjFijo(e.target.checked)}
                    className="accent-fluor-verde"
                  />
                  Se cobra todos los meses
                </label>
              </div>
              <div className="flex items-end">
                <Boton
                  variante="secundario"
                  onClick={() => {
                    const codigo = ajCodigo.trim();
                    const cantidad = Number(ajCantidad.replace(/\./g, '').replace(',', '.'));
                    if (!codigo || !Number.isFinite(cantidad) || cantidad === 0) return;
                    setAjustes((p) => ({
                      ...p,
                      [codigo]: {
                        cantidad,
                        motivo: ajMotivo.trim() || 'sin motivo',
                        ...(ajFijo ? { fijo: true } : {}),
                      },
                    }));
                    setAjCodigo('');
                    setAjCantidad('');
                    setAjMotivo('');
                    setAjFijo(false);
                  }}
                >
                  Agregar
                </Boton>
              </div>
            </div>
          )}
        </div>
      </Tarjeta>

      <Tarjeta>
        <TarjetaTitulo
          accion={
            epCerrado ? (
              <Chip nivel={bloqueado ? 'VERDE' : 'AMARILLO'}>
                {bloqueado ? `Cerrado · EP N° ${epCerrado.numero}` : 'Reabierto'}
              </Chip>
            ) : (
              <Chip nivel="AMARILLO">Sin cerrar</Chip>
            )
          }
        >
          Cierre del período
        </TarjetaTitulo>

        <div className="space-y-3 p-4">
          {errorCierre.length > 0 && (
            <ul className="space-y-1 rounded-control border border-fluor-rojo/40 bg-fluor-rojo/10 px-3 py-2 text-xs text-fluor-rojo">
              {errorCierre.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {avisoCierre && (
            <p className="rounded-control border border-fluor-verde/40 bg-fluor-verde/10 px-3 py-2 text-xs text-fluor-verde">
              {avisoCierre}
            </p>
          )}

          {bloqueado && epCerrado ? (
            <>
              <p className="text-xs leading-relaxed text-texto-mute">
                Cerrado el {ffecha((epCerrado.cerradoEn ?? '').slice(0, 10))} con la UF a{' '}
                {clp(epCerrado.valorUf)}. Lo que ves es lo que se emitió: no cambia aunque después
                se corrijan precios o guías. Sus {epCerrado.guiaIds.length.toLocaleString('es-CL')}{' '}
                guías están marcadas como facturadas.
              </p>
              {puede('estadoPago') && (
                <Boton
                  variante="peligro"
                  icono={<LockOpen size={15} />}
                  onClick={() => void reabrir()}
                  disabled={cerrando}
                >
                  Reabrir para corregir
                </Boton>
              )}
            </>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-texto-mute">
                Cerrar guarda este EP tal como está —partidas, precios y UF de hoy— y marca{' '}
                {guiasACerrar.length.toLocaleString('es-CL')} guías como facturadas, para que no
                vuelvan a cobrarse en el siguiente. El acumulado anterior del próximo EP sale de
                acá. Se puede reabrir cuando haga falta.
              </p>
              {puede('estadoPago') && (
                <Boton
                  variante="primario"
                  icono={<Lock size={15} />}
                  onClick={() => void cerrar()}
                  disabled={cerrando || !lineasConMovimiento.length}
                >
                  {cerrando ? 'Cerrando…' : `Cerrar el EP N° ${numeroEp || '—'}`}
                </Boton>
              )}
            </>
          )}

          {eps.length > 0 && (
            <div className="border-t border-linea pt-3">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-texto-mute">
                Estados de pago guardados
              </div>
              <div className="flex flex-wrap gap-1.5">
                {eps.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => {
                      setDesde(e.periodoDesde);
                      setHasta(e.periodoHasta);
                      setNumeroEp(String(e.numero));
                      setAjustes(e.ajustes ?? {});
                      setAvisoCierre(null);
                      setErrorCierre([]);
                    }}
                    className="rounded-control border border-linea bg-petroleo-800/40 px-2.5 py-1 text-[11px] text-texto-mute transition-colors hover:bg-white/5 hover:text-texto"
                  >
                    N° {e.numero} · {ffecha(e.periodoDesde)} a {ffecha(e.periodoHasta)} ·{' '}
                    {uf(e.totales?.totalUf ?? 0)} UF
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </Tarjeta>

      {contrato ? (
        <Alerta nivel={contrato.nivel} titulo={contrato.mensaje} />
      ) : (
        <Alerta nivel="NEUTRO" titulo="Contrato sin configurar">
          El estado de pago declara cuánto se lleva consumido del contrato, y ese dato todavía no
          está cargado. Se configura en Administración; hasta entonces no se puede exportar.
        </Alerta>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Indicador
          etiqueta="Total del período"
          valor={uf(epMostrado.totales.totalUf)}
          unidad="UF"
          nivel="VERDE"
        />
        <Indicador etiqueta="Neto" valor={clp(epMostrado.totales.totalClp)} />
        <Indicador etiqueta="IVA 19%" valor={clp(epMostrado.totales.ivaClp)} />
        <Indicador
          etiqueta="Total a facturar"
          valor={clp(epMostrado.totales.totalFacturarClp)}
          nivel="VERDE"
        />
      </div>

      {/* Lo que falta en las guías de este período, antes de cerrarlo. */}
      {!bloqueado && <Pendientes guias={guiasDelPeriodo} titulo="Revisar antes de cerrar" />}

      {epMostrado.rezagadas.length > 0 && (
        <Alerta
          nivel="AMARILLO"
          titulo={`${epMostrado.rezagadas.length} guía${epMostrado.rezagadas.length === 1 ? '' : 's'} de meses anteriores entra${epMostrado.rezagadas.length === 1 ? '' : 'n'} en este EP`}
        >
          Se cargaron después de que su período ya estaba cerrado, así que pasan a este mes
          contable. Es la regla de siempre: lo que llega después del cierre se cobra en el
          siguiente. Al cerrar quedan marcadas y no vuelven a aparecer.
        </Alerta>
      )}

      {/* Lo que el cálculo tuvo que dejar afuera, y por qué. Se llamaba igual
          que el panel de documentos incompletos, y con dos tarjetas del mismo
          nombre una se pasa por alto. */}
      {epMostrado.advertencias.length > 0 && (
        <Tarjeta>
          <TarjetaTitulo
            accion={<Chip nivel="AMARILLO">{epMostrado.advertencias.length} avisos</Chip>}
          >
            Qué quedó fuera del cálculo
          </TarjetaTitulo>
          <div className="space-y-1.5 p-4">
            {epMostrado.advertencias.map((a) => (
              <div key={a} className="flex items-start gap-2 text-xs text-fluor-amarillo">
                <TriangleAlert size={13} className="mt-0.5 shrink-0" />
                {a}
              </div>
            ))}
          </div>
        </Tarjeta>
      )}

      <Suspense
        fallback={
          <Tarjeta>
            <div className="p-4 text-xs text-texto-mute">Cargando…</div>
          </Tarjeta>
        }
      >
        <Plantilla datos={datosPlantilla} habilitado={lineasConMovimiento.length > 0} />
        <div className="h-4" />
        <ExportarPdf datos={datosEp} habilitado={lineasConMovimiento.length > 0} />
      </Suspense>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Físico-financiero por capítulo */}
        <Tarjeta className="lg:col-span-2">
          <TarjetaTitulo>
            Avance físico-financiero · {ffecha(desde)} a {ffecha(hasta)}
          </TarjetaTitulo>

          {lineasConMovimiento.length === 0 ? (
            <Vacio titulo="Sin movimiento en el período">
              Carga guías con fecha dentro del rango y el estado de pago se arma solo.
            </Vacio>
          ) : (
            <div className="divide-y divide-linea/60">
              {epMostrado.totales.porCapitulo.map((c) => {
                const lineas = lineasConMovimiento.filter(
                  (l) => l.capituloNumero === c.capituloNumero,
                );
                const abierto = expandido === c.capituloNumero;
                return (
                  <div key={c.capituloNumero}>
                    <button
                      type="button"
                      onClick={() => setExpandido(abierto ? null : c.capituloNumero)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[.03]"
                    >
                      <span className="font-mono text-xs tabular-nums text-texto-mute">
                        {c.capituloNumero}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm">{c.nombre}</span>
                      <span className="shrink-0 font-mono text-sm tabular-nums">
                        {uf(c.montoUf)}
                      </span>
                    </button>

                    {abierto && (
                      <div className="bg-petroleo-900/40 px-4 pb-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                              <th className="py-1.5 font-medium">Partida</th>
                              <th className="py-1.5 font-medium">Descripción</th>
                              <th className="py-1.5 text-right font-medium">Cantidad</th>
                              <th className="py-1.5 text-right font-medium">Precio UF</th>
                              <th className="py-1.5 text-right font-medium">Monto UF</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineas.map((l) => (
                              <tr key={l.itemContratoId} className="border-t border-linea/40">
                                <td className="py-1.5 pr-2 font-mono tabular-nums">
                                  {l.itemCodigo}
                                </td>
                                <td className="max-w-[16rem] truncate py-1.5 pr-2">
                                  {l.descripcion}
                                </td>
                                <td className="py-1.5 pr-2 text-right font-mono tabular-nums">
                                  {uf(l.cantPeriodo, 2)}
                                </td>
                                <td className="py-1.5 pr-2 text-right font-mono tabular-nums text-texto-mute">
                                  {uf(l.precioUf)}
                                </td>
                                <td className="py-1.5 text-right font-mono tabular-nums">
                                  {uf(l.montoPeriodoUf)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex items-center justify-between bg-petroleo-900/50 px-4 py-3">
                <span className="text-sm font-semibold uppercase tracking-wide">Total del EP</span>
                <span className="font-mono text-lg font-semibold tabular-nums text-fluor-verde">
                  {uf(epMostrado.totales.totalUf)}{' '}
                  <span className="text-xs text-texto-mute">UF</span>
                </span>
              </div>
            </div>
          )}
        </Tarjeta>

        {/* HES */}
        <Tarjeta className="h-fit">
          <TarjetaTitulo>Resumen HES</TarjetaTitulo>
          <div className="divide-y divide-linea/50">
            {hes.filas.map((f) => (
              <div
                key={f.numero}
                className={cn(
                  'flex items-center gap-2 px-4 py-1.5 text-xs',
                  f.montoUf === 0 && 'text-texto-mute/60',
                )}
              >
                <span className="w-5 shrink-0 font-mono tabular-nums text-texto-mute">
                  {f.numero}
                </span>
                <span className="min-w-0 flex-1 truncate">{f.nombre}</span>
                <span className="shrink-0 font-mono tabular-nums">{uf(f.montoUf)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1 border-t border-linea px-4 py-3 text-xs">
            <div className="flex justify-between">
              <span className="text-texto-mute">Total UF</span>
              <span className="font-mono tabular-nums">{uf(hes.totalUf)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-texto-mute">Valor UF</span>
              <span className="font-mono tabular-nums">{clp(hes.valorUf)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-texto-mute">Neto</span>
              <span className="font-mono tabular-nums">{clp(hes.totalClp)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-texto-mute">IVA 19%</span>
              <span className="font-mono tabular-nums">{clp(hes.ivaClp)}</span>
            </div>
            <div className="flex justify-between border-t border-linea pt-1.5 font-semibold">
              <span>A facturar</span>
              <span className="font-mono tabular-nums text-fluor-verde">
                {clp(hes.totalFacturarClp)}
              </span>
            </div>
          </div>
        </Tarjeta>
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip>{guias.length} guías en el sistema</Chip>
        <Chip nivel={epMostrado.guiasSinPartida.length ? 'AMARILLO' : 'VERDE'}>
          {epMostrado.guiasSinPartida.length} sin partida
        </Chip>
      </div>
    </div>
  );
}
